// frontend/src/components/AdCreativeTools/AdCreativeTools.jsx

import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { AnalysisContext } from "../../contexts/AnalysisContext";
import { AuthContext } from "../../contexts/AuthContext";
import { UIContext } from "../../contexts/UIContext";
import { ChromePicker } from "react-color";
import {
    Button,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    InputAdornment,
    Slider,
    TextField,
    CircularProgress,
    Switch,
    Stack,
    Alert
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ReplayIcon from "@mui/icons-material/Replay";
import InfoIcon from "@mui/icons-material/Info";
import ColorizeIcon from "@mui/icons-material/Colorize";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import HeatmapOverlay from "./HeatmapOverlay";
import { FormControlLabel } from "@mui/material";
import "./AdCreativeTools.css";
import LicenseModal from "./LicenseModal";
import DownloadProgressDonut from '../DownloadProgressDonut/DownloadProgressDonut';

// --- TabPanel Helper ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`ad-tools-tabpanel-${index}`}
            aria-labelledby={`ad-tools-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// --- Position Styles ---
const POSITION_STYLES = {
    top_right: { top: 0, right: 0 },
    top_left: { top: 0, left: 0 },
    bottom_right: { bottom: 0, right: 0 },
    bottom_left: { bottom: 0, left: 0 },
};


// --- URL Validation Helper ---
const isValidUrl = (urlString) => {
    if (
        !urlString ||
        typeof urlString !== "string" ||
        urlString.trim().length === 0
    ) {
        return false;
    }
    try {
        const url = new URL(urlString);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
        return false;
    }
};

// Takes a score from 0.0 to 1.0 and returns a color on a red-yellow-green gradient.
const getScoreGradientColor = (score) => {
    if (score === null) return "#888"; // Default grey
    // Hue goes from 0 (red) to 120 (green).
    // We cap the score at 0.9 to prevent pure green, which can be hard to see.
    const hue = Math.min(score, 0.9) * 120;
    // Consistent saturation and lightness for a "dark" theme.
    const saturation = "70%";
    const lightness = "35%";
    return `hsl(${hue}, ${saturation}, ${lightness})`;
};

const isMediaFileUrl = (url) => {
    if (!url) return false;
    // Simple check for common video file extensions
    return /\.(mp4|webm|mov|avi|mkv)$/i.test(url);
};

const isSocialUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be|tiktok\.com|instagram\.com|twitter\.com|x\.com|facebook\.com/i.test(url);
};

// --- COMPONENT DEFINITION: Now accepts props ---
const AdCreativeTools = ({ suggestionData }) => {
    const { t, i18n } = useTranslation();
    const { setIsPageBlurred, openDocViewer } = useContext(UIContext);
    const {
        analysisResults,
        contentPreviewUrl,
        content,
        contentSourceUrl,
        contentType,
        originalInputUrl,
        tasks,
        includeRegions,
        includeExplanation
    } = useContext(AnalysisContext);
    const { fetchWithAuth, token, user } = useContext(AuthContext);
    const isVideo = contentType === 'video';
    const isUrlInput = isValidUrl(originalInputUrl);
    const [isVideoAnalysisLoading, setIsVideoAnalysisLoading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);

    const getInitialConfigState = React.useCallback(() => ({
        type: "qr",
        style: "qr_black",
        shieldColor: "#2E4053",
        positionKey: "top_right",
        paddingX: 0,
        paddingY: 0,
        size: 10,
        qrPadding: 0,
        framePhrase: user?.analysis_preferences?.default_qr_label !== undefined 
            ? user.analysis_preferences.default_qr_label 
            : t("adCreativeTools.defaults.framePhrase"),
        frameFont: "Roboto",
        labelPosition: "auto",
        useRelativeMargins: false,
    }), [t, user?.analysis_preferences?.default_qr_label]);

    const [config, setConfig] = useState(getInitialConfigState());
    // Disable sizing/labels if it's a Video Shield OR an Invisible Watermark (which covers the whole image naturally)
    const isSizingTabDisabled = (isVideo && config.type === 'shield') || config.type === 'invisible';
    const [activeTab, setActiveTab] = useState(0);
    const [displayColorPicker, setDisplayColorPicker] = useState(false);
    const colorPickerRef = useRef(null);
    const [activePicker, setActivePicker] = useState(null);
    const isEyeDropperSupported = "EyeDropper" in window;
    const [isLoading, setIsLoading] = useState(false);
    const [generatedSnippet, setGeneratedSnippet] = useState("");
    const [copySuccess, setCopySuccess] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [generationSuccessMsg, setGenerationSuccessMsg] = useState(null);
    const [adImageUrl, setAdImageUrl] = useState("");
    const [imageDimensions, setImageDimensions] = useState(null);
    const [intrinsicDimensions, setIntrinsicDimensions] = useState(null);
    const [videoDuration, setVideoDuration] = useState(null);
    const [isSocialSource, setIsSocialSource] = useState(false);
    const [previewStyle, setPreviewStyle] = useState({});
    const [isQrCodeLoading, setIsQrCodeLoading] = useState(true);
    const [imageElement, setImageElement] = useState(null);
    const imageRefCallback = React.useCallback((node) => {
        setImageElement(node);
    }, []);
    const [suggestedConfig, setSuggestedConfig] = useState(null);
    const [isSuggesting] = useState(false);
    const [heatmapData, setHeatmapData] = useState(null);
    const [heatmapSrc, setHeatmapSrc] = useState(null);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const [heatmapThreshold, setHeatmapThreshold] = useState(0);
    const [colorPalette, setColorPalette] = useState([]);
    const [videoCornerScores, setVideoCornerScores] = useState(null);
    const [urlVerificationStatus, setUrlVerificationStatus] = useState('idle');
    const [urlVerificationError, setUrlVerificationError] = useState('');
    const verificationDebounceTimeoutRef = useRef(null);
    const translatedContentType = t(`contentInput.contentTypes.${contentType}`);
    // --- STATE FOR LICENSING ---
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [licenseStatus, setLicenseStatus] = useState(null); // { status: 'active'|'inactive'|null, expires_at: date }
    const [pendingConfigForGeneration, setPendingConfigForGeneration] = useState(null);
    const [isCheckingLicense, setIsCheckingLicense] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    // --- Pricing State (Reused from Selector/Billing) ---
    const [pricingConfig, setPricingConfig] = useState(null);
    const [currencyRates, setCurrencyRates] = useState({ USD: 1 });

    const isBrowserSupportedVideo = isVideo && (
        content instanceof File
            ? ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'].includes(content.type) || /\.(mp4|webm|ogg|mov)$/i.test(content.name)
            : !/\.(avi|mkv|flv|wmv)$/i.test(originalInputUrl || contentSourceUrl)
    );

    // Options defined inside the component to access `t`
    const STYLE_OPTIONS = {
        qr: [
            { value: "qr_black", label: t("adCreativeTools.styleOptions.qr_black") },
            { value: "qr_white", label: t("adCreativeTools.styleOptions.qr_white") },
        ],
    };

    const POSITION_OPTIONS = [
        { value: "top_right", label: t("adCreativeTools.positionOptions.top_right") },
        { value: "top_left", label: t("adCreativeTools.positionOptions.top_left") },
        { value: "bottom_right", label: t("adCreativeTools.positionOptions.bottom_right") },
        { value: "bottom_left", label: t("adCreativeTools.positionOptions.bottom_left") },
    ];


    const activeResult = useMemo(() => {
        // Prefer the primary scores, then fall back to any other result.
        // This is a more stable way to get the main result object.
        return (
            analysisResults.digital_integrity_score ||
            analysisResults.vetra_score ||
            Object.values(analysisResults).find(
                (res) => res && res.exact_hash,
            ) ||
            {}
        );
    }, [analysisResults]);

    const exact_hash = activeResult?.exact_hash;
    const context_hash = activeResult?.context_hash;

    // --- EFFECT: Blur Background when Modal Opens ---
    useEffect(() => {
        setIsPageBlurred(isLicenseModalOpen);
        return () => setIsPageBlurred(false);
    }, [isLicenseModalOpen, setIsPageBlurred]);

    // --- Load Pricing Config on Mount ---
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const [priceRes, rateRes] = await Promise.all([
                    fetchWithAuth('/api/config/billing'),
                    fetchWithAuth('/api/user/billing?limit=1')
                ]);
                if (priceRes.ok) setPricingConfig(await priceRes.json());
                if (rateRes.ok) {
                    const rateData = await rateRes.json();
                    if (rateData.currency_rates) setCurrencyRates(rateData.currency_rates);
                }
            } catch (err) {
                console.error("Failed to load pricing data:", err);
            }
        };
        loadConfig();
    }, [fetchWithAuth]);

    const initializedMediaRef = useRef(null);

    useEffect(() => {
        // Prevent resetting the user's config state (e.g. reverting to shield/qr) when only translations ('t') change
        const currentMediaKey = isVideo ? content : suggestionData;
        if (initializedMediaRef.current === currentMediaKey) {
            return;
        }
        initializedMediaRef.current = currentMediaKey;
        // This effect will now handle BOTH image and video initialization
        if (isVideo) {
            // --- START VIDEO ASYNC WORKFLOW ---
            const analyzeVideoPlacement = async () => {
                setIsVideoAnalysisLoading(true);
                setGenerationError(null);
                try {
                    const formData = new FormData();
                    formData.append("video_file", content); // `content` holds the video File object

                    const response = await fetchWithAuth("/api/media/video-placement-suggestion", {
                        method: "POST",
                        body: formData,
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || t('adCreativeTools.errors.videoPlacementFailed'));
                    }

                    const data = await response.json();
                    setVideoCornerScores(data.suggestions.cornerScores || null);

                    const { suggestions } = data;
                    const videoDefaultConfig = {
                        ...getInitialConfigState(),
                        type: isUrlInput ? 'shield' : 'qr',
                        positionKey: suggestions.positionKey,
                        shieldColor: suggestions.shieldColor,
                        size: 10,
                    };
                    setConfig(videoDefaultConfig);
                    setSuggestedConfig(videoDefaultConfig);
                    setColorPalette(suggestions.colorPalette || []);

                } catch (err) {
                    console.error("Video placement analysis failed:", err);
                    setGenerationError(err.message);
                    // Fallback to default config on error
                    setConfig({ ...getInitialConfigState(), type: isUrlInput ? 'shield' : 'qr', size: 10 });
                } finally {
                    setIsVideoAnalysisLoading(false);
                }
            };

            analyzeVideoPlacement();
            // Your existing logic for images (suggestionData exists)
        } else if (suggestionData && !suggestionData.error) {
            setVideoCornerScores(null); 
            const {
                suggestions,
                heatmapData: scoringData,
                heatmapImage,
            } = suggestionData;
            const isFromUrl = contentSourceUrl && isValidUrl(contentSourceUrl);
            const defaultType = isFromUrl ? "shield" : "qr";

            const newSuggestedConfig = {
                ...getInitialConfigState(),
                type: defaultType,
                positionKey: suggestions.positionKey,
                size: suggestions.size,
                shieldColor: suggestions.shieldColor,
            };

            setSuggestedConfig(newSuggestedConfig);
            setConfig(newSuggestedConfig);
            setHeatmapSrc(heatmapImage);
            setHeatmapData(scoringData);
            setColorPalette(suggestions.colorPalette);
            setGenerationError(null);
        } else if (suggestionData?.error) {
            setGenerationError(suggestionData.error);
            const isFromUrl = contentSourceUrl && isValidUrl(contentSourceUrl);
            const defaultType = isFromUrl ? "shield" : "qr";
            setConfig({ ...getInitialConfigState(), type: defaultType });
        }
    }, [suggestionData, isVideo, content, fetchWithAuth, contentSourceUrl, isUrlInput, getInitialConfigState, t]);



    // This effect now handles BOTH setting the initial URL and resetting verification status.
    useEffect(() => {
        setAdImageUrl(contentSourceUrl);

        // When the source URL changes, determine the initial verification state.
        if (contentSourceUrl && isValidUrl(contentSourceUrl)) {
            setUrlVerificationStatus('verified'); // If it came from the context, it's already verified.
        } else {
            setUrlVerificationStatus('idle');
        }
        setUrlVerificationError(''); // Clear any old errors.

    }, [contentSourceUrl]);

    // --- Automatically switch tabs if the current one becomes disabled ---
    useEffect(() => {
        // Case 1: If the type switches to 'shield', the 'Label' tab (index 2) is always disabled.
        if (config.type === "shield" && activeTab === 2) {
            setActiveTab(0);
        }

        // Case 2: If the type switches to 'shield' AND it is a video, the 'Sizing' tab (index 1) is disabled.
        if (isVideo && config.type === "shield" && activeTab === 1) {
            setActiveTab(0);
        }
    }, [config.type, activeTab, isVideo]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                displayColorPicker &&
                colorPickerRef.current &&
                !colorPickerRef.current.contains(event.target)
            ) {
                handleColorPickerClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [displayColorPicker]);

    useEffect(() => {
        // This effect runs whenever the config object or the adImageUrl changes.
        // If a snippet has already been generated, it clears it,
        // forcing the user to re-generate with the new settings.
        if (generatedSnippet) {
            setGeneratedSnippet("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, adImageUrl]); // VETRA_AI_IGNORE_LINE


    // Debounced URL Verification Logic
    const handleAdImageUrlChange = (e) => {
        const newUrl = e.target.value;
        setAdImageUrl(newUrl);

        // Clear previous debounce timer
        if (verificationDebounceTimeoutRef.current) {
            clearTimeout(verificationDebounceTimeoutRef.current);
        }

        // Reset status if input is cleared or invalid
        if (!newUrl.trim() || !isValidUrl(newUrl)) {
            setUrlVerificationStatus('idle');
            setUrlVerificationError('');
            return;
        }

        // Set status to 'verifying' immediately for user feedback
        setUrlVerificationStatus('verifying');
        setUrlVerificationError('');

        // Set a new debounce timer
        verificationDebounceTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await fetchWithAuth('/api/re-verify-hash', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: newUrl, contentType: contentType }) // <-- Pass contentType
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || t('adCreativeTools.errors.verificationFailed'));
                }

                if (data.exact_hash === exact_hash) {
                    setUrlVerificationStatus('verified');
                    setUrlVerificationError('');
                } else {
                    setUrlVerificationStatus('failed');
                    setUrlVerificationError(t('adCreativeTools.errors.hashMismatch'));
                }
            } catch (error) {
                setUrlVerificationStatus('failed');
                setUrlVerificationError(error.message);
            }
        }, 800); // 800ms debounce delay
    };


    // --- DYNAMICALLY DETERMINE THE SIZE SLIDER'S LABEL TEXT ---
    let dimensionWord = t("adCreativeTools.controls.dimensionWordShorter"); // A safe default before the image loads.
    if (imageDimensions) {
        // Once the image has loaded, determine which dimension is smaller.
        dimensionWord =
            imageDimensions.width < imageDimensions.height ? t("adCreativeTools.controls.dimensionWordWidth") : t("adCreativeTools.controls.dimensionWordHeight");
    }
    const sizeLabelText = t("adCreativeTools.controls.sizeLabel", { size: config.size, dimensionWord: dimensionWord });

    // --- Centralized Constraint Logic (Memoized) ---
    // We move this logic up so it can be used by both the Effect and the Render
    const { sliderMin, sliderMax, sliderTooltip } = useMemo(() => {
        let min = 4;
        let max = 50;
        let tooltip = "";

        if (intrinsicDimensions && intrinsicDimensions.width > 0) {
            const shorterSide = Math.min(intrinsicDimensions.width, intrinsicDimensions.height);

            if (config.type === "qr") {
                max = 50; // QR Cap

                // QR Minimum: 48px
                const minPercentFor48px = (48 / shorterSide) * 100;
                const safeMin = Math.ceil(minPercentFor48px);

                // Cap min at 50 so slider doesn't break if image is tiny
                min = Math.min(safeMin, 50);

                tooltip = t("adCreativeTools.tooltips.qrSizeConstraint", { min: min });
            } else {
                // SHIELD Logic
                // Shield Minimum: 16px (Standard icon size / Clickable area)
                const minPercentFor16px = (16 / shorterSide) * 100;

                // Ensure we don't go below the hardcoded floor of 4%
                min = Math.max(4, Math.ceil(minPercentFor16px));

                tooltip = t("adCreativeTools.tooltips.shieldSizeConstraint", { min: min });
            }
        } else {
            // Default fallback if dimensions aren't loaded yet
            min = config.type === "qr" ? 10 : 4;
        }

        return { sliderMin: min, sliderMax: max, sliderTooltip: tooltip };

    }, [config.type, intrinsicDimensions, t]);

    // --- Auto-Correction Effect ---
    // This ensures that if the calculated minimum jumps up (e.g., to 15%),
    // the state automatically updates from 10% to 15%.
    useEffect(() => {
        let newSize = config.size;

        if (newSize < sliderMin) {
            newSize = sliderMin;
        } else if (newSize > sliderMax) {
            newSize = sliderMax;
        }

        if (newSize !== config.size) {
            setConfig(prev => ({ ...prev, size: newSize }));
        }
    }, [config.type, sliderMin, sliderMax, config.size]);

    // This hook replaces the old `onLoad` handler. It continuously watches the
    // image element for size changes (including zoom) and updates the state.
    useEffect(() => {
        if (!imageElement) return;

        const updateDimensions = () => {
            const rect = imageElement.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                setImageDimensions({ width: rect.width, height: rect.height });
                // The fallback div doesn't fire standard media load events, 
                // so we populate the intrinsic dimensions here to ensure the slider math works.
                if (isVideo && !isBrowserSupportedVideo) {
                    setIntrinsicDimensions({ width: rect.width, height: rect.height });
                }
            }
        };

        const observer = new ResizeObserver(() => {
            updateDimensions();
        });

        observer.observe(imageElement);
        
        // Force an immediate measurement to prevent the QR from hiding on first render tick
        updateDimensions();

        return () => {
            observer.disconnect();
        };
    }, [imageElement, isVideo, isBrowserSupportedVideo]);

    // --- THIS IS THE PREVIEW STYLE LOGIC ---
    useEffect(() => {
        // Case 1: Video VAST Shield Preview (fixed size for player compatibility)
        if (isVideo && config.type === 'shield') {
            const style = {
                ...POSITION_STYLES[config.positionKey],
                width: '32px',
                height: '32px',
            };
            // Use dimensions if available to calculate accurate pixel-based padding
            if (imageDimensions) {
                const shorterSide = Math.min(imageDimensions.width, imageDimensions.height);
                const paddingXPx = shorterSide * (config.paddingX / 100.0);
                const paddingYPx = shorterSide * (config.paddingY / 100.0);
                style.top = config.positionKey.includes("top") ? `${paddingYPx}px` : "auto";
                style.bottom = config.positionKey.includes("bottom") ? `${paddingYPx}px` : "auto";
                style.left = config.positionKey.includes("left") ? `${paddingXPx}px` : "auto";
                style.right = config.positionKey.includes("right") ? `${paddingXPx}px` : "auto";
            }
            setPreviewStyle(style);
            return;
        }

        // Case 2: If dimensions are not yet known, hide the overlay to prevent stretching/flickering.
        // This is why onLoadedMetadata is crucial—it provides the dimensions to get past this check.
        if (!imageDimensions || imageDimensions.width === 0) {
            setPreviewStyle({ display: 'none' });
            return;
        }

        // Case 3: Dimensions ARE known. Calculate dynamic size for all QR codes and Image Shields.
        const { width, height } = imageDimensions;
        const shorterSide = Math.min(width, height);

        // The QR part's dimension is controlled exclusively by the 'size' slider.
        const qrDimensionPx = shorterSide * (config.size / 100.0);

        let frameWidth = qrDimensionPx;
        let frameHeight = qrDimensionPx;

        // If a label exists, only the frame's height increases. Width remains fixed.
        if (config.type === "qr" && config.framePhrase.trim() !== "") {
            frameHeight = qrDimensionPx / 0.82;
        }

        const finalStyle = {
            ...POSITION_STYLES[config.positionKey],
            width: `${frameWidth}px`,
            height: `${frameHeight}px`,
        };

        // This margin logic is correct and applies to the final calculated frame.
        if (config.useRelativeMargins) {
            finalStyle.top = config.positionKey.includes("top") ? `${config.paddingY}%` : "auto";
            finalStyle.bottom = config.positionKey.includes("bottom") ? `${config.paddingY}%` : "auto";
            finalStyle.left = config.positionKey.includes("left") ? `${config.paddingX}%` : "auto";
            finalStyle.right = config.positionKey.includes("right") ? `${config.paddingX}%` : "auto";
        } else {
            const paddingXPx = shorterSide * (config.paddingX / 100.0);
            const paddingYPx = shorterSide * (config.paddingY / 100.0);
            finalStyle.top = config.positionKey.includes("top") ? `${paddingYPx}px` : "auto";
            finalStyle.bottom = config.positionKey.includes("bottom") ? `${paddingYPx}px` : "auto";
            finalStyle.left = config.positionKey.includes("left") ? `${paddingXPx}px` : "auto";
            finalStyle.right = config.positionKey.includes("right") ? `${paddingXPx}px` : "auto";
        }

        setPreviewStyle(finalStyle);

    }, [
        // The dependency array is correct and includes everything needed.
        config.positionKey,
        config.size,
        config.paddingX,
        config.paddingY,
        config.useRelativeMargins,
        config.type,
        config.framePhrase,
        imageDimensions,
        isVideo,
    ]);

    useEffect(() => {
        // When the hash or the component switches to QR mode,
        // we must reset the loading state to 'true' to show the spinner.
        // The 'onLoad' event of the <img> tag will then set it back to 'false'.
        if (config.type === "qr" && exact_hash) {
            setIsQrCodeLoading(true);
        }
    }, [exact_hash, config.type]);

    // --- Append the ref parameter ---
    const attributionParam = user?.public_id ? `&ref=${user.public_id}` : '';
    const verificationUrl = `/verify?id=${activeResult.exact_hash}${attributionParam}`;

    const handleSliderChange = (name, value) => {
        setConfig((prevConfig) => ({ ...prevConfig, [name]: value }));
    };

    const handleEyeDropper = async () => {
        if (!isEyeDropperSupported) return;
        try {
            const eyeDropper = new window.EyeDropper();
            const result = await eyeDropper.open();
            setConfig((prevConfig) => ({
                ...prevConfig,
                shieldColor: result.sRGBHex,
            }));
        } catch (e) {
            console.log("Eyedropper selection cancelled.");
        }
    };

    // --- COLOR PICKER LOGIC (CORRECTED) ---
    const handlePickerOpen = (e, pickerName) => {
        // Instead of a className, we check for the button's unique aria-label.
        // This is more reliable as it doesn't depend on how MUI structures the component's internals.
        // If the click happened on the eyedropper button or any of its children, we exit early.
        if (e.target.closest('[aria-label="pick color from screen"]')) {
            return;
        }
        setActivePicker(pickerName);
        setDisplayColorPicker(true);
    };

    const handleColorPickerClose = () => {
        setDisplayColorPicker(false);
        setActivePicker(null);
    };

    const handleColorChange = (color) => {
        const newColor =
            color.rgb.a === 1
                ? color.hex
                : `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`;

        if (activePicker === "label") {
            setConfig((prevConfig) => ({
                ...prevConfig,
                frameColor: newColor,
            }));
        } else if (activePicker === "shield") {
            setConfig((prevConfig) => ({
                ...prevConfig,
                shieldColor: newColor,
            }));
        }
    };

    const handleRelativeMarginsChange = (event) => {
        setConfig((prev) => ({
            ...prev,
            useRelativeMargins: event.target.checked,
        }));
    };

    const handleConfigChange = (event) =>
        setConfig((prev) => ({
            ...prev,
            [event.target.name]: event.target.value,
        }));

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleReset = () => {
        // If we have an AI suggestion, reset to that.
        if (suggestedConfig) {
            // Preserve the current 'type' from the user's selection.
            setConfig({ ...suggestedConfig, type: config.type });
        } else {
            // Fallback reset: also preserves the current type.
            const newConfig = { ...getInitialConfigState(), type: config.type };
            if (config.type === "qr") {
                newConfig.style = STYLE_OPTIONS.qr[0].value;
            }
            setConfig(newConfig);
        }

        setActiveTab(0);
        setGeneratedSnippet("");
        setGenerationError(null);
        setHeatmapThreshold(0);
    };

    const currentStyleOptions = STYLE_OPTIONS[config.type] || [];
    const isStyleValid = currentStyleOptions.some(
        (option) => option.value === config.style,
    );
    const safeStyleValue = isStyleValid
        ? config.style
        : currentStyleOptions[0]?.value || "";

    // We are removing the complex inner divs that were causing the sizing bug.
    const renderPreviewOverlay = () => {
        // 1. Construct Query Strings for Link
        const contextQuery = context_hash ? `ctx=${context_hash}` : '';
        const tasksQueryForLink = tasks.map(t => `context=${encodeURIComponent(t)}`).join('&');
        const optionsQuery = `regions=${includeRegions}&explanation=${includeExplanation}`;

        // 2. Construct STABLE Dynamic QR Image URL (for visual)
        // We use URLSearchParams without a timestamp so the browser caches the image.
        const qrParams = new URLSearchParams();
        
        if (user?.public_id) qrParams.append('ref', user.public_id);
        if (context_hash) qrParams.append('ctx', context_hash);
        if (tasks && tasks.length > 0) {
            tasks.forEach(t => qrParams.append('tasks', t));
        }
        qrParams.append('regions', includeRegions);
        qrParams.append('explanation', includeExplanation);
        
        const dynamicQrCodeUrl = `/api/qr/${exact_hash}?${qrParams.toString()}`;

        // 3. Construct Verification Link (for click)
        // /verify?id=HASH&ctx=...&context=...&regions=...&explanation=...
        let finalVerificationUrl = verificationUrl; // Base is /verify?id=HASH (derived in component body)

        // We need to make sure we don't duplicate params if verificationUrl already has them (though it shouldn't yet)
        const linkParts = [];
        if (!finalVerificationUrl.includes('ctx=') && contextQuery) linkParts.push(contextQuery);
        if (tasksQueryForLink) linkParts.push(tasksQueryForLink);
        linkParts.push(optionsQuery);


        // --- Add source=shield ONLY if type is shield ---
        if (config.type === "shield") {
            linkParts.push("source=shield");
        }

        if (linkParts.length > 0) {
            finalVerificationUrl += `&${linkParts.join('&')}`;
        }

        const dynamicShieldUrl = "/api/media/shield-template";
        let overlayContent;

        const isHeatmapVisible = showHeatmap;

        const tooltipTitle =
            config.type === "qr" ? (
                <div className="preview-tooltip-content">
                    <span className="tooltip-primary-text">
                        {t("adCreativeTools.previewTooltips.qrPrimary")}
                    </span>
                    <span className="tooltip-warning-text">
                        {t("adCreativeTools.previewTooltips.qrWarning")}
                    </span>
                </div>
            ) : config.type === "shield" ? (
                t("adCreativeTools.previewTooltips.shield")
            ) : (
                "" // Empty string disables the tooltip for the invisible watermark
            );

        if (config.type === "invisible") {
            // Extract RGB from hex to create a transparent filigrana gradient
            const rgbMatch = config.shieldColor.match(/\w\w/g);
            let r = 255, g = 255, b = 255;
            if (rgbMatch && rgbMatch.length >= 3) {
                const [rr, gg, bb] = rgbMatch.map(x => parseInt(x, 16));
                r = isNaN(rr) ? 255 : rr; g = isNaN(gg) ? 255 : gg; b = isNaN(bb) ? 255 : bb;
            }
            const gradientColor = `rgba(${r}, ${g}, ${b}, 0.25)`; // 25% opacity of chosen shield color

            overlayContent = (
                <>
                    <div 
                        className="invisible-watermark-pattern" 
                        style={{
                            background: `repeating-linear-gradient(-45deg, ${gradientColor}, ${gradientColor} 15px, transparent 15px, transparent 30px)`
                        }}
                    />
                    <div className="invisible-watermark-badge">
                        <div className="invisible-badge-default">
                            <VisibilityOffIcon fontSize="small" /> 
                            {t("adCreativeTools.previewTooltips.invisibleActive", "Invisible Watermark Active")}
                        </div>
                        <div className="invisible-badge-hover">
                            <VisibilityIcon fontSize="small" /> 
                            {t("adCreativeTools.previewTooltips.qrPrimary", "Click to test verification link!")}
                        </div>
                    </div>
                </>
            );
            
            // Force exact 100% overlay with no padding to prevent white corners
            previewStyle.width = '100%';
            previewStyle.height = '100%';
            previewStyle.top = '0';
            previewStyle.left = '0';
            previewStyle.right = 'auto';
            previewStyle.bottom = 'auto';

        } else if (config.type === "shield") {
            overlayContent = (
                <div
                    className="preview-overlay-image"
                    style={{
                        backgroundColor: config.shieldColor,
                        maskImage: `url(${dynamicShieldUrl})`,
                        WebkitMaskImage: `url(${dynamicShieldUrl})`,
                    }}
                />
            );
        } else { // 'qr' logic with the new flexbox layout
            const isLabelVisible = config.framePhrase.trim() !== "";
            const isInverted = config.style === 'qr_white';
            const frameBgColor = isInverted ? 'black' : 'white';
            const labelColor = isInverted ? 'white' : 'black';
            const paddingValue = config.qrPadding / 100.0;
            const scaleValue = 1.0 - paddingValue * 2;

            const isQrAtTop = config.positionKey.includes("top");
            const finalLabelPosition =
                config.labelPosition === "auto"
                    ? isQrAtTop ? "bottom" : "top"
                    : config.labelPosition;

            // --- Automatic Font Size Calculation ---
            let finalFontSize = '12px'; // Default fallback
            let finalPadding = '0 5%';  // Default padding
            if (previewStyle.width) {
                const containerWidth = parseFloat(previewStyle.width);
                if (!isNaN(containerWidth) && containerWidth > 0) {
                    // 1. Define a safe text area (e.g., 90% of the container width) to account for padding
                    const maxTextWidth = containerWidth * 0.90;

                    // 2. Estimate font size based on text length and an average character width factor.
                    // Formula derived from: textLength * fontSize * factor = maxTextWidth
                    const textLength = Math.max(config.framePhrase.length, 1); // Avoid division by zero
                    const averageCharWidthFactor = 0.6; // Heuristic for average character width to font size ratio
                    let calculatedFontSize = maxTextWidth / (textLength * averageCharWidthFactor);

                    // 3. Clamp the font size to prevent it being too large for short text
                    const maxFontSize = containerWidth * 0.18; // Max 18% of the container width
                    calculatedFontSize = Math.min(calculatedFontSize, maxFontSize);
                    finalFontSize = `${calculatedFontSize}px`;
                }
            }

            overlayContent = (
                <div
                    className="preview-content-stack"
                    style={{
                        backgroundColor: frameBgColor,
                        flexDirection: finalLabelPosition === 'top' ? 'column-reverse' : 'column',
                    }}
                >
                    {/* QR Code Part */}
                    <div
                        className="preview-aspect-ratio-box"
                        style={{
                            position: 'relative', // Anchor for absolute children
                            width: '100%',        // Fill the frame width defined by previewStyle
                            flex: 1,              // Take remaining vertical space if label exists
                            display: 'flex',
                            overflow: 'hidden'    // Contain any spillover
                        }}
                    >
                        {isQrCodeLoading && (
                            <div
                                className="preview-qr-frame"
                                style={{
                                    backgroundColor: frameBgColor,
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 5
                                }}
                            >
                                {/* Responsive size: 50% of container, but capped at 24px to look neat */}
                                <CircularProgress
                                    size={20}
                                    sx={{
                                        width: '50% !important',
                                        height: '50% !important',
                                        maxWidth: '24px',
                                        maxHeight: '24px',
                                        color: isInverted ? 'black' : 'primary.main' // Ensure spinner is visible against bg
                                    }}
                                    className="qr-loading-spinner"
                                />
                            </div>
                        )}
                        <img
                            src={dynamicQrCodeUrl}
                            alt="QR Code Preview"
                            className="preview-overlay-image"
                            onLoad={() => setIsQrCodeLoading(false)}
                            onError={(e) => {
                                console.error("QR Code Image Failed to Load", e);
                                setIsQrCodeLoading(false);
                            }}
                            style={{
                                transform: `scale(${scaleValue})`,
                                filter: isInverted ? "invert(1)" : "none",
                                opacity: isQrCodeLoading ? 0 : 1,
                                transition: 'opacity 0.2s ease-in-out',
                                width: '100%',  // Ensure image fits container
                                height: '100%', // Ensure image fits container
                                objectFit: 'contain'
                            }}
                        />
                    </div>

                    {/* Label Part */}
                    {isLabelVisible && (
                        <div
                            className="preview-label-text"
                            style={{
                                color: labelColor,
                                backgroundColor: frameBgColor,
                                fontFamily: config.frameFont,
                                fontSize: finalFontSize,
                                minHeight: '18%', // Use minHeight instead of fixed height to prevent text cutoff
                                // VETRA_AI_IGNORE_NEXT
                                padding: finalPadding,  // Use simple horizontal padding
                                alignItems: 'center',
                                // padding: '0 5%',
                            }}
                        >
                            {config.framePhrase}
                        </div>
                    )}
                </div>
            );
        }
        

        return (
            <Tooltip
                title={tooltipTitle}
                arrow
                placement="top"
                PopperProps={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 0],
                            },
                        },
                    ],
                }}
            >
                <a
                    href={finalVerificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`preview-overlay ${config.type === 'invisible' ? 'invisible-mode' : ''}`}
                    style={{ ...previewStyle, opacity: isHeatmapVisible ? 0.5 : 1.0 }}
                    onClick={(e) => {
                        // Prevent default to handle opening manually if needed, 
                        // or let the <a> tag handle it natively.
                        // Actually, for a pure link, we should just let the <a> tag work.
                        // But if you have specific logic (like stopping propagation), keep preventDefault
                        // and use window.open with the CORRECT url.
                        e.preventDefault();
                    }}
                    onMouseUp={(e) => {
                        // Only trigger on left click (button 0)
                        if (e.button === 0) {
                            // --- CLICK TARGETING FIX ---
                            // If invisible mode, ignore clicks that are not on the specific button
                            if (config.type === 'invisible' && !e.target.closest('.invisible-badge-hover')) {
                                return; 
                            }

                            let targetUrl = finalVerificationUrl;

                            // --- TIME BOMB LOGIC ---
                            // If this is a SHIELD, we must simulate the ad behavior
                            // by appending the current timestamp.
                            if (config.type === 'shield') {
                                const separator = targetUrl.includes('?') ? '&' : '?';
                                targetUrl = `${targetUrl}${separator}click_ts=${Date.now()}`;
                            }

                            window.open(targetUrl, "_blank");
                        }
                    }}
                >
                    {overlayContent}
                </a>
            </Tooltip>
        );
    };

    const handleGenerateAssets = async (currentConfig, isSilent = false) => {

        if (!isSilent) {
            setIsLoading(true); // Only show main UI loading if NOT in modal
            setDownloadProgress(0);
        }

        if (isSocialUrl(adImageUrl)) {
            setIsSocialSource(true);
        } else {
            setIsSocialSource(false);
        }

        // If silent (called from modal payment), we don't want to reset main UI loading/progress yet
        // because the modal handles it. But we do want to reset error state.
        if (!isSilent) {
            setGeneratedSnippet("");
            setCopySuccess(false);
            setGenerationError(null);
            setGenerationSuccessMsg(null);
        }

        const snapshotConfig = {
            ...currentConfig,
            context_hash: context_hash,
            tasks: tasks,
            includeRegions: includeRegions,
            includeExplanation: includeExplanation
        };

        // --- PROGRESS SIMULATION ---
        // We adjust the speed based on content type.
        // Video burning takes time (~20s), so we increment slowly.
        // Image generation is fast, so we increment quickly.
        const progressInterval = setInterval(() => {
            setDownloadProgress((oldProgress) => {
                if (oldProgress >= 99) {
                    return 99; // Stall at 99% if backend is still processing
                }
                
                // If Video: Increment by tiny amounts (0.5 to 1.5%)
                // If Image: Increment by larger amounts (1 to 10%)
                const minInc = isVideo ? 0.5 : 1;
                const maxInc = isVideo ? 1.5 : 10;
                
                const diff = Math.random() * (maxInc - minInc) + minInc;
                return Math.min(oldProgress + diff, 99);
            });
        }, 500);

        try {
            // --- VAST Generation Logic ---
            if (isVideo && currentConfig.type === 'shield') {

                // Determine the URL type
                const inputUrlType = isMediaFileUrl(adImageUrl) ? 'media_file' : 'vast_tag';
                // We MUST use contentSourceUrl, which is the resolved MP4 URL from the proxy.
                const vastResponse = await fetchWithAuth("/api/media/generate-vast-wrapper", {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        originalVastUrl: adImageUrl,
                        config: snapshotConfig,
                        inputUrlType: inputUrlType,
                        duration: videoDuration || 30.0
                    })
                });

                if (!vastResponse.ok) {
                    const errorData = await vastResponse.json();
                    throw new Error(errorData.error || t('adCreativeTools.errors.vastFailed'));
                }

                const vastXmlString = await vastResponse.text();
                setGeneratedSnippet(vastXmlString); // Reuse the snippet state to display the XML

            } else if (currentConfig.type === "qr" || currentConfig.type === "invisible") {
                const formData = new FormData();
                if (isVideo) {
                    formData.append("video_file", content);
                } else {
                    formData.append("image_file", content);
                }
                const configPayload = {
                    ...snapshotConfig,
                    exact_hash: activeResult.exact_hash,
                    contentType: contentType,
                    language: i18n.language
                };
                formData.append("config", JSON.stringify(configPayload));

                // Clear dummy interval since we are using real tracking now
                clearInterval(progressInterval);

                // --- HYBRID PROGRESS TRACKING VIA XHR ---
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("POST", "/api/media/watermark", true);
                    xhr.withCredentials = true; // Send Auth cookies

                    let processingInterval;

                    // 1. Track Real-Time Upload Progress (0% to 20%)
                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            const percentComplete = (event.loaded / event.total) * 20;
                            setDownloadProgress(percentComplete);
                        }
                    };

                    // 2. Simulate Backend Processing Progress (20% to 90%)
                    xhr.upload.onloadend = () => {
                        // Upload is done. Start the processing simulation from 20%
                        setDownloadProgress(20);
                        
                       processingInterval = setInterval(() => {
                            setDownloadProgress((prev) => {
                                if (prev >= 90) return 90; // Stall at 90% until download starts
                                
                                // Video is slow (~10-15s with 'superfast' preset), so increment by ~2.5% per tick
                                // Image is fast, so increment by ~15% per tick
                                return prev + (isVideo ? 2.5 : 15);
                           });
                        }, 500);
                    };

                    // 3. Track Real-Time Download Progress (90% to 100%)
                    xhr.onprogress = (event) => {
                        if (processingInterval) clearInterval(processingInterval);
                        if (event.lengthComputable) {
                            // If the server tells us exactly how big the file is
                            const percentComplete = 90 + ((event.loaded / event.total) * 10);
                           setDownloadProgress(percentComplete);
                        } else {
                            // Fallback: If Nginx streams the file in chunks without a content-length
                            setDownloadProgress(prev => Math.min(prev + 1, 98));
                        }
                    };

                    xhr.responseType = "blob";

                    // 4. Handle Completion
                    xhr.onload = () => {
                        if (processingInterval) clearInterval(processingInterval);

                        // --- LOGIC FOR STATUS 202 ---
                        if (xhr.status === 202) {
                            setDownloadProgress(100);
                            const reader = new FileReader();
                            reader.onload = () => {
                                // FORCE the frontend to use the translation file, ignoring backend message
                                setGenerationSuccessMsg(t("adCreativeTools.notifications.emailed"));
                                
                                // --- AUTO-DISMISS AFTER 8 SECONDS ---
                                setTimeout(() => {
                                    setGenerationSuccessMsg(null);
                                }, 8000);
                                
                                resolve();
                            };
                            reader.readAsText(xhr.response);

                        // --- NORMAL SYNCHRONOUS DOWNLOAD (200-299, excluding 202) ---
                        } else if (xhr.status >= 200 && xhr.status < 300) {
                           setDownloadProgress(100);

                            const finalPlacement = xhr.getResponseHeader('X-Final-Placement');
                            if (isVideo && finalPlacement) {
                                setConfig(prev => ({ ...prev, positionKey: finalPlacement }));
                            }

                            const disposition = xhr.getResponseHeader('Content-Disposition');
                            let downloadName = isVideo 
                                ? (currentConfig.type === 'invisible' ? 'video-secured.mp4' : 'video-verified.mp4')
                                : (currentConfig.type === 'invisible' ? 'creative-secured.png' : 'creative-verified.png');
                            if (disposition) {
                                const filenameRegex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?/;
                                const matches = filenameRegex.exec(disposition);
                                if (matches && matches[1]) {
                                    downloadName = decodeURIComponent(matches[1]);
                                }
                            }

                            const blob = xhr.response;
                            const downloadUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = downloadUrl;
                            link.setAttribute("download", downloadName);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(downloadUrl);
                            
                            resolve(); 
                        } else {
                            // SAFELY HANDLE HTML TIMEOUT ERRORS
                            const reader = new FileReader();
                            reader.onload = () => {
                                try {
                                    // Try to parse JSON. If it's HTML, this will fail safely.
                                    const errorData = JSON.parse(reader.result);
                                    reject(new Error(errorData.error || t("adCreativeTools.errors.watermarkFailed")));
                                } catch (e) {
                                    reject(new Error("Server timeout. Video processing took too long. Try using Bulk Analysis for large files."));
                                }
                            };
                            reader.readAsText(xhr.response);
                        }
                    };

                    xhr.onerror = () => {
                        if (processingInterval) clearInterval(processingInterval);
                        reject(new Error("Network connection lost during generation."));
                    };

                    xhr.send(formData);
                });

            } else if (currentConfig.type === "shield" && !isVideo) {
                // ... (shield logic is unchanged and correct)
                if (!isValidUrl(adImageUrl)) {
                    throw new Error(t("adCreativeTools.errors.snippetUrlRequired"));
                }
                const snippetResponse = await fetchWithAuth(
                    "/api/media/generate-snippet",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            exact_hash: activeResult.exact_hash,
                            context_hash: context_hash,
                            tasks: tasks,
                            include_regions: includeRegions,
                            include_explanation: includeExplanation,
                            position_key: currentConfig.positionKey,
                            size: currentConfig.size,
                            padding_x: currentConfig.paddingX,
                            padding_y: currentConfig.paddingY,
                            shieldColor: currentConfig.shieldColor,
                            adImageUrl: adImageUrl,
                            useRelativeMargins: currentConfig.useRelativeMargins,
                        }),
                    },
                );
                if (!snippetResponse.ok) {
                    const errorData = await snippetResponse.json();
                    throw new Error(
                        errorData.error || t("adCreativeTools.errors.snippetFailed")
                    );
                }
                const snippetData = await snippetResponse.json();
                setGeneratedSnippet(snippetData.generated_code);
            }
        } catch (error) {
            console.error("Error generating creative assets:", error);
            const errorMessage = error.message || t("adCreativeTools.errors.watermarkFailed");
            setGenerationError(errorMessage.replace('image', contentType));
            clearInterval(progressInterval);
            setDownloadProgress(0);
        } finally {
            if (!isSilent) setIsLoading(false);
            // Delay resetting progress if NOT in silent mode so user sees 100%
            // In silent mode (modal), the modal close handles UI reset
            if (!isSilent) {
                setTimeout(() => {
                    setDownloadProgress(0);
                }, 500);
            }
        }
    };

    const handleCopySnippet = () => {
        navigator.clipboard.writeText(generatedSnippet).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    // --- Handler to test the snippet in a new tab ---
    const handleTestSnippet = () => {
        if (!generatedSnippet) return;

        const isVast = generatedSnippet.trim().startsWith('<?xml');

        if (isVast) {
            const wrapperXmlString = generatedSnippet;
            const backendBaseUrl = window.location.origin;

            // --- 1. Dynamic Aspect Ratio Logic ---
            let playerWidth = 640;
            let playerHeight = 360;

            if (intrinsicDimensions && intrinsicDimensions.width > 0 && intrinsicDimensions.height > 0) {
                const aspectRatio = intrinsicDimensions.width / intrinsicDimensions.height;
                const MAX_HEIGHT = 500; 
                
                if (intrinsicDimensions.height > MAX_HEIGHT) {
                    playerHeight = MAX_HEIGHT;
                    playerWidth = Math.round(MAX_HEIGHT * aspectRatio);
                } else {
                    playerHeight = intrinsicDimensions.height;
                    playerWidth = intrinsicDimensions.width;
                }
            }

            // --- 2. The Final "Brute Force" Fix ---
            const isShieldBottomLeft = config.positionKey === 'bottom_left';
            
            const timerFixScript = isShieldBottomLeft ? `
                <script>
                    (function() {
                        // Custom countdown display
                        let customCountdown = document.createElement('div');
                        customCountdown.id = 'custom-ad-countdown';
                        customCountdown.style.cssText = 'position: absolute; left: auto; bottom: 0px; right: 0px; vertical-align: middle; text-align: initial; display: inline-block; pointer-events: auto; color: #fff; opacity: 0.9; background-color: transparent; cursor: default; padding: 5px 5px; font-size: 11px; z-index: 9999; font-family: arial, sans-serif;;';
                        document.getElementById('adContainer').appendChild(customCountdown);

                        // Update countdown from ad manager
                        window.updateCustomCountdown = function(remainingTime) {
                            if (customCountdown && remainingTime > 0) {
                                const totalSeconds = Math.ceil(remainingTime);
                                const minutes = Math.floor(totalSeconds / 60);
                                const seconds = totalSeconds % 60;
                                const formattedTime = minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
                                customCountdown.textContent = 'Ad: (' + formattedTime + ')';
                                customCountdown.style.display = 'block';
                            } else if (customCountdown) {
                                customCountdown.style.display = 'none';
                            }
                        };
                    })();
                </script>
            ` : '';

            const vastTesterHtml = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Live VAST Preview</title>
                    <style>
                        body { font-family: sans-serif; background-color: #f0f2f5; margin: 0; padding: 40px; text-align: center; }
                        
                        #mainContainer { 
                            width: ${playerWidth}px; 
                            height: ${playerHeight}px; 
                            margin: 20px auto; 
                            border: 2px dashed #ccc; 
                            background-color: black; 
                            box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
                            position: relative; 
                        }
                        
                        #contentElement { width: 100%; height: 100%; background-color: black; }
                        #adContainer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
                        .overlay-ui { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10; }
                        .overlay-ui button { padding: 10px 20px; font-size: 16px; cursor: pointer; border: 2px solid white; background-color: rgba(0,0,0,0.7); color: white; border-radius: 5px; }
                        .overlay-ui button:hover { background-color: rgba(0,0,0,0.9); }
                        #loadingIndicator { display: none; width: 80px; height: 80px; border: 8px solid rgba(255, 255, 255, 0.2); border-top-color: white; border-radius: 50%; animation: spin 1s linear infinite; }
                        @keyframes spin { to { transform: rotate(360deg); } }
                        
                        #errorLog { max-width: ${playerWidth}px; margin: 20px auto; padding: 15px; background-color: #ffebee; border: 1px solid #c62828; color: #c62828; text-align: left; white-space: pre-wrap; word-wrap: break-word; display: none; box-sizing: border-box; }
                        #debugLog { max-width: ${playerWidth}px; margin: 20px auto; padding: 15px; background-color: #e3f2fd; border: 1px solid #1976d2; color: #1976d2; text-align: left; white-space: pre-wrap; word-wrap: break-word; font-size: 12px; box-sizing: border-box; }
                    </style>
                    <script src="//imasdk.googleapis.com/js/sdkloader/ima3.js"></script>
                </head>
                <body>
                    <h1>Live VAST Preview</h1>
                    <div id="mainContainer">
                        <video id="contentElement" playsinline controlslist="nodownload"></video>
                        <div id="adContainer"></div>
                        <div id="uiContainer" class="overlay-ui">
                            <div id="loadingIndicator"></div>
                            <button id="playButton">Play Ad</button>
                        </div>
                    </div>
                    <div id="errorLog"></div>
                    <div id="debugLog"></div>

                    <!-- INJECT JS FIX -->
                    ${timerFixScript}

                    <script>
                        const backendOutputXml = \`${wrapperXmlString.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
                        
                        let adDisplayContainer, adsLoader, adsManager;
                        const videoElement = document.getElementById('contentElement');
                        const adContainer = document.getElementById('adContainer');
                        const uiContainer = document.getElementById('uiContainer');
                        const playButton = document.getElementById('playButton');
                        const loadingIndicator = document.getElementById('loadingIndicator');
                        const errorLog = document.getElementById('errorLog');
                        const debugLog = document.getElementById('debugLog');

                        function log(message) {
                            console.log(message);
                            debugLog.textContent += message + '\\n';
                        }

                        function isWrapper(xmlString) { return xmlString.includes("<VASTAdTagURI>"); }

                        async function playAd() {
                            log('--- New Ad Request ---');
                            playButton.style.display = 'none';
                            loadingIndicator.style.display = 'block';
                            errorLog.style.display = 'none';
                            adContainer.style.display = 'block';
                            videoElement.pause();
                            videoElement.src = '';
                            videoElement.load();
                            
                            try {
                                let finalVastForIma;
                                const isWrapperResponse = isWrapper(backendOutputXml);
                                log(isWrapperResponse ? 'Detected VAST Wrapper response.' : 'Detected VAST Inline response.');

                                if (isWrapperResponse) {
                                    const parser = new DOMParser();
                                    const wrapperDoc = parser.parseFromString(backendOutputXml, "text/xml");
                                    const vastAdTagURI = wrapperDoc.querySelector("VASTAdTagURI")?.textContent.trim();
                                    const iconsNode = wrapperDoc.querySelector("Icons");

                                    if (!vastAdTagURI) throw new Error("Invalid wrapper: Missing VASTAdTagURI");

                                    const proxyUrl = \`${backendBaseUrl}/api/proxy/file\`;
                                    const response = await fetch(proxyUrl, {
                                        method: 'POST',
                                        headers: { 
                                            'Content-Type': 'application/json',
                                            'Authorization': 'Bearer ${token}'
                                        },
                                        body: JSON.stringify({ url: vastAdTagURI, contentType: 'text' })
                                    });

                                    if (!response.ok) {
                                        const errData = await response.json();
                                        throw new Error(\`Failed to fetch downstream VAST: \${errData.error || response.statusText}\`);
                                    }
                                    
                                    const finalVastString = await response.text();
                                    const finalVastDoc = parser.parseFromString(finalVastString, "text/xml");                                    
                                    const linearNode = finalVastDoc.querySelector("Linear");

                                    if (linearNode && iconsNode) {
                                        linearNode.prepend(iconsNode.cloneNode(true));
                                    }

                                    finalVastForIma = new XMLSerializer().serializeToString(finalVastDoc);
                                    log('Final stitched VAST ready for IMA.');

                                } else {
                                    finalVastForIma = backendOutputXml;
                                    log('Using Inline VAST directly for IMA.');
                                }

                                adDisplayContainer = new google.ima.AdDisplayContainer(adContainer, videoElement);
                                adDisplayContainer.initialize();
                                
                                adsLoader = new google.ima.AdsLoader(adDisplayContainer);
                                adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
                                adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);
                                
                                const adsRequest = new google.ima.AdsRequest();
                                adsRequest.adsResponse = finalVastForIma;
                                adsRequest.linearAdSlotWidth = ${playerWidth};
                                adsRequest.linearAdSlotHeight = ${playerHeight};
                                
                                adsLoader.requestAds(adsRequest);
                            } catch (error) {
                                onAdError({ getError: () => error });
                            }
                        }

                        function onAdsManagerLoaded(adsManagerLoadedEvent) {
                            log('Ads manager loaded.');
                            loadingIndicator.style.display = 'none';
                            const adsRenderingSettings = new google.ima.AdsRenderingSettings();
                            adsRenderingSettings.useStyledLinearAds = true;
                            ${isShieldBottomLeft ? `
                            // Hide the default countdown UI
                            adsRenderingSettings.uiElements = []; // This removes default UI elements
                            ` : ''}
                            
                            adsManager = adsManagerLoadedEvent.getAdsManager(videoElement, adsRenderingSettings);
                            
                            adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, () => { adsManager.setVolume(1); });
                            adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
                            adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, onAllAdsCompleted);
                            ${isShieldBottomLeft ? `
                            // Update custom countdown on each ad progress event
                            adsManager.addEventListener(google.ima.AdEvent.Type.AD_PROGRESS, (e) => {
                                const adData = e.getAdData();
                                if (window.updateCustomCountdown && adData) {
                                    window.updateCustomCountdown(adData.duration - adData.currentTime);
                                }
                            });
                            ` : ''}
                            
                            try {
                                adsManager.init(${playerWidth}, ${playerHeight}, google.ima.ViewMode.NORMAL);
                                adsManager.start();
                            } catch (adError) {
                                onAdError({ getError: () => adError });
                            }
                        }

                        function onAllAdsCompleted() {
                            log('All ads completed. Presenting replay option.');
                            cleanupIma();
                            playButton.textContent = 'Replay Ad';
                            playButton.style.display = 'block';
                        }

                        function onAdError(adErrorEvent) {
                            const error = adErrorEvent ? adErrorEvent.getError() : null;
                            let errorMessage = 'Ad Error: ' + (error ? (error.getMessage ? error.getMessage() : error.toString()) : 'Unknown Error');
                            log('ERROR: ' + errorMessage);
                            
                            errorLog.textContent = errorMessage;
                            errorLog.style.display = 'block';
                            loadingIndicator.style.display = 'none';
                            playButton.textContent = 'Retry';
                            playButton.style.display = 'block';
                            cleanupIma();
                        }
                        
                        function cleanupIma() {
                            if (adsManager) { adsManager.destroy(); adsManager = null; }
                            if (adsLoader) { adsLoader.contentComplete(); adsLoader = null; }
                            if (adDisplayContainer) { adDisplayContainer.destroy(); adDisplayContainer = null; }
                        }

                        playButton.addEventListener('click', playAd);
                        log('VAST Tester ready.');
                    </script>
                </body>
                </html>
                `;
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.open();
                newWindow.document.write(vastTesterHtml);
                newWindow.document.close();
            } else {
                alert(t('adCreativeTools.snippetModal.popupWarning'));
            }
        } else {
            // For HTML snippets, create a full HTML document to ensure proper rendering.
            const htmlContent = `
                    <!DOCTYPE html>
                    <html lang="en">
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <title>${t('adCreativeTools.snippetModal.testTitle')}</title>
                        <style>
                            body { 
                                font-family: sans-serif; 
                                background-color: #f0f2f5; 
                                padding: 40px;
                                text-align: center;
                            }
                            .ad-wrapper { 
                                max-width: 800px;
                                margin: 20px auto;
                                border: 2px dashed #ccc; 
                                padding: 10px;
                                background-color: white;
                                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                                display: inline-block;
                            }
                        </style>
                    </head>
                    <body>
                        <h1>${t('adCreativeTools.snippetModal.livePreview')}</h1>
                        <div class="ad-wrapper">
                            ${generatedSnippet}
                        </div>
                    </body>
                    </html>
                `;

            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.open();
                newWindow.document.write(htmlContent);
                newWindow.document.close();
            } else {
                alert(t('adCreativeTools.snippetModal.popupWarning'));
            }
        }
    };

    const isUrlVerified = urlVerificationStatus === 'verified';
    const isShieldAndUrlInvalid = config.type === "shield" && !isUrlVerified;
    const isInitializing = isSuggesting || isVideoAnalysisLoading;
    const isGenerateButtonDisabled =
        isLoading ||
        isInitializing ||
        isShieldAndUrlInvalid ||
        (config.type === "qr" && isQrCodeLoading);

    let generateButtonTooltipText = "";
    if (config.type === "shield" && !isUrlVerified) {
        if (urlVerificationStatus === 'verifying') {
            generateButtonTooltipText = t('adCreativeTools.tooltips.verifyingUrl');
        } else if (urlVerificationStatus === 'failed') {
            generateButtonTooltipText = urlVerificationError || t('adCreativeTools.errors.urlNotVerified');
        } else {
            generateButtonTooltipText = t("adCreativeTools.tooltips.invalidUrl");
        }
    } else if (config.type === "qr" && isQrCodeLoading) {
        generateButtonTooltipText = t("adCreativeTools.tooltips.qrLoading");
    }

    const handleVideoMetadataLoad = (event) => {
        const { clientWidth, clientHeight, videoWidth, videoHeight, duration } = event.target;
        // Set rendered dimensions for the overlay CSS
        setImageDimensions({ width: clientWidth, height: clientHeight });
        // Set real dimensions for the math
        setIntrinsicDimensions({ width: videoWidth, height: videoHeight });
        setVideoDuration(duration);
    };

    const handleImageLoad = (event) => {
        const { naturalWidth, naturalHeight } = event.target;
        setIntrinsicDimensions({ width: naturalWidth, height: naturalHeight });
    };

    // --- LIVE SCORE CALCULATION ---
    // 1. Helper Function
    const calculateScoreForPosition = (pKey, currentConfig, imgDims, gridData, thresholdVal) => {
        if (!gridData || !imgDims) return null;

        const { width, height } = imgDims;
        const gridSize = gridData.length;
        const { size, paddingX, paddingY, useRelativeMargins } = currentConfig;

        const shorterSide = Math.min(width, height);
        const shieldSizePx = shorterSide * (size / 100.0);

        const shieldWidthAsPercent = width > 0 ? shieldSizePx / width : 0;
        const shieldHeightAsPercent = height > 0 ? shieldSizePx / height : 0;

        let paddingXPercent = 0;
        let paddingYPercent = 0;
        if (useRelativeMargins) {
            paddingXPercent = paddingX / 100.0;
            paddingYPercent = paddingY / 100.0;
        } else {
            const paddingXPx = shorterSide * (paddingX / 100.0);
            const paddingYPx = shorterSide * (paddingY / 100.0);
            if (width > 0) paddingXPercent = paddingXPx / width;
            if (height > 0) paddingYPercent = paddingYPx / height;
        }

        let startXPercent = paddingXPercent;
        let startYPercent = paddingYPercent;
        if (pKey.includes("right")) {
            startXPercent = 1.0 - shieldWidthAsPercent - paddingXPercent;
        }
        if (pKey.includes("bottom")) {
            startYPercent = 1.0 - shieldHeightAsPercent - paddingYPercent;
        }

        const startCol = Math.floor(startXPercent * gridSize);
        const endCol = Math.ceil((startXPercent + shieldWidthAsPercent) * gridSize);
        const startRow = Math.floor(startYPercent * gridSize);
        const endRow = Math.ceil((startYPercent + shieldHeightAsPercent) * gridSize);

        let maxBusyness = 0;
        const normalizedThreshold = thresholdVal / 100.0; // Convert 0-100 to 0.0-1.0

        for (let r = startRow; r < endRow; r++) {
            for (let c = startCol; c < endCol; c++) {
                if (r >= 0 && r < gridSize && c >= 0 && c < gridSize) {
                    const pixelValue = gridData[r][c];
                    
                    // APPLY THE SLIDER THRESHOLD
                    // If the pixel's busyness is below the threshold, it is hidden/ignored
                    if (pixelValue >= normalizedThreshold) {
                        maxBusyness = Math.max(maxBusyness, pixelValue);
                    }
               }
            }
        }

        return 1.0 - maxBusyness;
    };

    // 2. Memoized Live Scores
    const liveCornerScores = useMemo(() => {
        if (!heatmapData || !imageDimensions) return {};
        
        const scores = {};
        const corners = ["top_left", "top_right", "bottom_left", "bottom_right"];
        
        corners.forEach(key => {
            // Pass the heatmapThreshold into the calculation
            const score = calculateScoreForPosition(key, config, imageDimensions, heatmapData, heatmapThreshold);
           if (score !== null) {
                scores[key] = Math.round(score * 100);
            }
        });
        
        return scores;
    }, [heatmapData, imageDimensions, config, heatmapThreshold]); // Added heatmapThreshold as a dependency

    // 3. Derived Current Score
    const placementScore = liveCornerScores[config.positionKey] !== undefined 
        ? liveCornerScores[config.positionKey] / 100 
        : null;

    if (!activeResult) return null;

    // We can show a loading screen for the entire toolset while the suggestion is running.

    if (isInitializing) {
        // MODIFIED: This block is changed to show only a spinner without a background.
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column", // Stack vertically
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    minHeight: "400px",
                    backgroundColor: "transparent",
                    gap: 2 // Add space
                }}
            >
                <CircularProgress color="secondary" />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500, paddingTop: '1rem'}}>
                    {t('adCreativeTools.loading.analyzingLayout')}
                </Typography>
            </Box>
        );
    }

    const scoreColor = getScoreGradientColor(placementScore);

    // --- LICENSE CHECK FUNCTION ---
    const checkLicense = async () => {
        if (!exact_hash) return null;
        try {
            // We can reuse the verification endpoint which returns license_info
            // Note: Ideally backend should have a dedicated lightweight check, but this works
            const response = await fetchWithAuth(`/api/verify/all/${exact_hash}`);
            if (response.ok) {
                const data = await response.json();
                const info = data.license_info;
                // If status is inactive AND there is no expiration date,
                // it implies a fresh asset (never licensed). Return null to trigger "New Activation" logic.
                if (info && info.status === 'inactive' && !info.expires_at) {
                    return null;
                }
                return info || null;
            }
        } catch (e) {
            console.error("License check failed", e);
        }
        return null;
    };

    // --- GENERATE HANDLER ---
    const handleGenerateClick = async (currentConfig) => {
        // 1. Shield/Snippet Logic (CPM) - Unchanged
        if (currentConfig.type === 'shield') {
            handleGenerateAssets(currentConfig);
            return;
        }

        // 2. QR Logic (Licensing)
        setIsLicenseModalOpen(true); // Open modal immediately
        setIsCheckingLicense(true); // Set loading state passed to modal
        try {
            const currentLicense = await checkLicense();
            setLicenseStatus(currentLicense);
            setPendingConfigForGeneration(currentConfig);
            setIsLicenseModalOpen(true);
        } catch (e) {
            console.error(e);
        } finally {
            setIsCheckingLicense(false); // Stop spinner
        }
    };

    // --- LICENSE CONFIRMATION HANDLER ---
    const handleLicensePayment = async (durationDays, costUnits) => {
        // We set paying state to show spinner on the button inside the modal
        setIsPaying(true); 

        try {
            // 1. Activate
            const response = await fetchWithAuth('/api/license/activate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    exact_hash: exact_hash,
                    content_type: contentType,
                    duration_days: durationDays,
                    context_hash: context_hash
                })
            });
            
            if (!response.ok) throw new Error("Activation failed");

            // 2. Close Modal Immediately upon success
            setIsLicenseModalOpen(false);
            setIsPaying(false); // Reset paying state

            // 3. Trigger Main Generation Flow
            // Pass 'true' for isSilent because the Modal handled the initial "Processing" UI.
            // But actually, we WANT the main loading indicator to show "Downloading...".
            // So we'll trigger it normally.
            if (pendingConfigForGeneration) {
                // We start the generation process which sets isLoading=true and handles the donut.
                handleGenerateAssets(pendingConfigForGeneration, false); 
            }
            
        } catch (e) {
            setGenerationError(e.message);
            setIsPaying(false); // Stop spinner if error
            // Keep modal open so user sees error? Or close and show toast?
            // Usually keeping it open with error message is better UX, but for now we'll just log it.
        }
    };

    // --- Download Handler (Passed to Modal) ---
    const handleModalDownload = () => {
        setIsLicenseModalOpen(false);
        // If we have a pending config, use it. Otherwise use current config.
        const configToUse = pendingConfigForGeneration || config;
        handleGenerateAssets(configToUse, false); // false = show loading UI
    };

    return (
        <div className="ad-creative-tools-container">
            <div className="tools-header">
                <div className="tools-title-container">
                    <Typography
                        variant="h6"
                        component="h3"
                        className="tools-title"
                    >
                        {t("adCreativeTools.title")}
                    </Typography>
                    <Tooltip
                        title={t("adCreativeTools.infoTooltip")}
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                    >
                        <IconButton size="small" className="info-icon-button">
                            <InfoIcon
                                fontSize="small"
                                className="custom-info-icon"
                            />
                        </IconButton>
                    </Tooltip>
                </div>
                <Stack direction="row" spacing={2} alignItems="center">

                    {/* --- Reset Button --- */}
                    {config.type !== "invisible" && (
                        <Tooltip title={t("adCreativeTools.resetTooltip")} arrow>
                            <Button
                                variant="outlined"
                                startIcon={<ReplayIcon />}
                                onClick={handleReset}
                                className="reset-button"
                            >
                                {t("adCreativeTools.resetButton")}
                            </Button>
                        </Tooltip>
                    )}

                    {/* --- Circular Score Indicator --- */}
                    {placementScore !== null && config.type !== "invisible" && (
                        <Tooltip
                            title={t("adCreativeTools.scoreTooltip", {
                                score: Math.round(placementScore * 100),
                            })}
                            arrow
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {/* Background circle */}
                                <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={40}
                                    sx={{ color: "rgba(0, 0, 0, 1)" }}
                                />
                                {/* Foreground (score) circle */}
                                <CircularProgress
                                    variant="determinate"
                                    value={placementScore * 100}
                                    size={40}
                                    sx={{
                                        color: scoreColor,
                                        position: "absolute",
                                        left: 0,
                                        transition: "color 0.3s ease-in-out", // Smooth color change
                                    }}
                                />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: "absolute",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        color="white"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        {`${Math.round(placementScore * 100)}%`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    )}

                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        className="type-switcher-container"
                    >
                        {/* 1. QR CODE */}
                        <Tooltip title={t("adCreativeTools.types.qr", "QR Code")} arrow placement="top">
                            <Box
                                component="img"
                                src="/static/previews/qr_icon.png"
                                alt="QR Code"
                                className={`type-switcher-icon ${config.type !== "qr" ? "unselected" : ""}`}
                                onClick={() => setConfig((prev) => ({ ...prev, type: "qr" }))}
                            />
                        </Tooltip>

                        {/* 2. SHIELD */}
                        <Tooltip 
                            title={isVideo && !isUrlInput ? t('adCreativeTools.tooltips.videoQrOnly') : t("adCreativeTools.types.shield", "Shield")} 
                            arrow 
                            placement="top"
                        >
                            <Box
                                component="img"
                                src="/static/previews/badge_template.png"
                                alt="Shield"
                                className={`type-switcher-icon ${config.type !== "shield" ? "unselected" : ""}`}
                                onClick={() => {
                                    if (isVideo && !isUrlInput) return;
                                    setConfig((prev) => ({ ...prev, type: "shield", size: 10 }));
                                }}
                                style={{
                                    // Change cursor to blocked circle if disabled, else normal pointer
                                    cursor: isVideo && !isUrlInput ? 'not-allowed' : 'pointer',
                                    // Make it look heavily greyed out/faded if disabled
                                    opacity: isVideo && !isUrlInput ? 0.15 : undefined 
                                }}
                            />
                        </Tooltip>

                        {/* 3. INVISIBLE WATERMARK */}
                        <Tooltip title={t("adCreativeTools.types.invisible", "Invisible Watermark")} arrow placement="top">
                            <VisibilityOffIcon 
                                className={`type-switcher-icon ${config.type !== "invisible" ? "unselected" : ""}`}
                                onClick={() => setConfig((prev) => ({ ...prev, type: "invisible" }))}
                                sx={{ 
                                    color: 'white', 
                                    fontSize: '26px !important',
                                }} 
                                aria-label={t("adCreativeTools.altText.invisibleType", "Invisible Watermark Type")}
                            />
                        </Tooltip>
                    </Stack>
                </Stack>
            </div>

            <div className="tools-main-content">
                <Box className="preview-area">
                    <div className="preview-controls">
                        <Typography variant="overline">
                            {t("adCreativeTools.livePreviewLabel")}
                        </Typography>
                        {heatmapSrc && config.type !== 'invisible' && (
                            <FormControlLabel className="heatmap-toggle" sx={{ flexShrink: 0 }} // Add this to prevent the switch from being squished
                                control={
                                    <Switch
                                        size="small"
                                        className="custom-explanation-switch"
                                        checked={showHeatmap}
                                        onChange={(e) =>
                                            setShowHeatmap(e.target.checked)
                                        }
                                    />
                                }
                                label={
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                        }}
                                    >
                                        <Typography variant="caption">
                                            {t("adCreativeTools.showHeatmapLabel")}
                                        </Typography>
                                        <Tooltip
                                            title={t("adCreativeTools.heatmapInfoTooltip")}
                                            arrow
                                            placement="top"
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                className="info-icon-button"
                                            >
                                                <InfoIcon
                                                    fontSize="small"
                                                    className="custom-info-icon"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        )}
                    </div>

                    {showHeatmap && heatmapSrc && config.type !== 'invisible' && (
                        <Box sx={{ pb: 1, width: '100%' }}> {/* Removed px: 2 to fix padding */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '6px' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                                    {t('adCreativeTools.controls.heatmapThresholdLabel', 'Confidence Threshold')}
                                </Typography>
                                <Tooltip
                                    title={t("adCreativeTools.tooltips.heatmapThreshold")}
                                    arrow
                                    placement="top"
                                    PopperProps={{ className: "global-dark-tooltip" }}
                                >
                                    <IconButton size="small" className="info-icon-button" sx={{ p: 0 }}>
                                        <InfoIcon fontSize="inherit" className="custom-info-icon" sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Slider
                                size="small"
                                value={heatmapThreshold}
                                onChange={(e, val) => setHeatmapThreshold(val)}
                                min={0}
                                max={100}
                                track="inverted"  // <-- Swaps the blue and grey colors on the track
                                valueLabelDisplay="auto"
                                valueLabelFormat={(value) => `${value}%`} // <-- Adds the % to the drag tooltip
                                sx={{
                                    color: 'var(--secondary-color)',
                                    padding: '8px 0',
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                    }
                                }}
                            />
                        </Box>
                    )}

                    <div className="preview-wrapper">
                        {isVideo ? (
                            isBrowserSupportedVideo ? (
                                <video
                                    ref={imageRefCallback}
                                    key={contentPreviewUrl}
                                    src={contentPreviewUrl}
                                    className="preview-image"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    onLoadedMetadata={handleVideoMetadataLoad}
                                    style={{
                                        filter: showHeatmap ? "grayscale(100%) opacity(0.2)" : "none",
                                        transition: "filter 0.3s ease, opacity 0.3s ease"
                                    }}
                                />
                            ) : (
                                <div
                                    ref={imageRefCallback}
                                    className="preview-image"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.4)', // Blends perfectly with app styling
                                        width: '100%',
                                        aspectRatio: '16/9',
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        textAlign: 'center',
                                        filter: showHeatmap ? "grayscale(100%) opacity(0.2)" : "none",
                                        transition: "filter 0.3s ease, opacity 0.3s ease"
                                    }}
                                >
                                    {/* Inner div holds the padding so ResizeObserver measures the outer box accurately */}
                                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <VideocamIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                        <Typography variant="body1" sx={{ color: 'white', mb: 1, fontWeight: 500 }}>
                                            {t('adCreativeTools.previewNotSupported.title')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ maxWidth: '90%', lineHeight: 1.4 }}>
                                            {t('adCreativeTools.previewNotSupported.description')}
                                        </Typography>
                                    </div>
                                </div>
                            )
                        ) : (
                            <img
                                ref={imageRefCallback}
                                key={contentPreviewUrl}
                                src={contentPreviewUrl}
                                alt={t('adCreativeTools.altText.creativePreview')}
                                className="preview-image"
                                onLoad={handleImageLoad}
                                // --- ADD THIS STYLE PROP ---
                                style={{
                                    filter: showHeatmap ? "grayscale(100%) opacity(0.2)" : "none",
                                    transition: "filter 0.3s ease, opacity 0.3s ease"
                                }}
                            />
                        )}
                        {heatmapSrc && imageDimensions && !isVideo && (
                            <HeatmapOverlay
                                heatmapSrc={heatmapSrc}
                                threshold={heatmapThreshold}
                                className={showHeatmap ? "visible" : ""}
                            />
                        )}
                        {renderPreviewOverlay()}
                    </div>
                    <div className={`heatmap-legend-container ${showHeatmap && config.type !== 'invisible' ? 'visible' : ''}`}>
                        <span className="legend-label">{t('adCreativeTools.heatmapLegend.ideal')}</span>
                        <div className="legend-bar" />
                        <span className="legend-label">{t('adCreativeTools.heatmapLegend.busy')}</span>
                    </div>
                    <Typography
                        variant="caption"
                        className="preview-disclaimer"
                        sx={{ mt: 1, display: 'block', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}
                    >
                        {config.type === 'shield' ? (
                            <Trans i18nKey="adCreativeTools.disclaimers.shield">
                                This asset uses <strong>CPM billing</strong>. Costs are accrued when the ad loads.
                            </Trans>
                        ) : config.type === 'invisible' ? (
                            <Trans i18nKey="adCreativeTools.disclaimers.invisible">
                                This asset embeds an invisible cryptographic Vetra-ID into the media's chroma channels. It is secure against cropping and compression.
                            </Trans>
                        ) : (
                            <Trans i18nKey="adCreativeTools.disclaimers.qr">
                                This asset requires a <strong>Content License</strong>. You will be prompted to activate it before downloading.
                            </Trans>
                        )}
                    </Typography>
                </Box>
                <Box className="controls-area">
                    {config.type === 'invisible' ? (
                        /* --- INVISIBLE WATERMARK EXPLANATION PANEL --- */
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '350px', justifyContent: 'space-between', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '12px', p: 3 }}>
                            
                            {/* --- CENTERED INFO CONTENT --- */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flexGrow: 1, justifyContent: 'center' }}>
                                <VisibilityOffIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
                                <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 600 }}>
                                    {t('adCreativeTools.invisibleWatermark.title', 'Invisible Watermark')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, maxWidth: '90%', lineHeight: 1.6 }}>
                                    {t('adCreativeTools.invisibleWatermark.description', 'This asset will embed a secure cryptographic ID directly into the pixels of your creative. No visual changes or padding are required.')}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    size="small"
                                    onClick={() => openDocViewer('user_guides/invisible_watermarking.md')}
                                    sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.8)' }}
                                >
                                    {t('adCreativeTools.invisibleWatermark.learnMore', 'Learn how it works')}
                                </Button>
                            </Box>

                            {/* --- BUTTON ANCHORED AT BOTTOM --- */}
                            <Box sx={{ width: '100%', mt: 4 }}>
                                <Tooltip title={generateButtonTooltipText} arrow placement="top">
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            fullWidth
                                            className="generate-button"
                                            onClick={() => handleGenerateClick(config)} 
                                            disabled={isGenerateButtonDisabled}
                                            startIcon={isLoading ? (isVideo ? <CircularProgress size={20} color="inherit" /> : <DownloadProgressDonut progress={downloadProgress} size={24} thickness={4.5} fontSize="0.5rem" color="inherit" />) : (isCheckingLicense ? <CircularProgress size={24} color="inherit" /> : <FileDownloadIcon />)}
                                        >
                                            {isCheckingLicense ? t('adCreativeTools.generateButton.checkingLicense') :
                                            (isLoading
                                                ? t("adCreativeTools.generateButton.generating")
                                                : t("adCreativeTools.generateButton.generateInvisible", "Secure & Download")
                                            )}
                                        </Button>
                                    </span>
                                </Tooltip>
                                {isLoading && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1.5, textAlign: "center", color: "rgba(255, 255, 255, 0.6)", fontStyle: "italic", animation: "pulse 2s infinite" }}>
                                        {isVideo ? t('adCreativeTools.loading.processingVideo') : t('adCreativeTools.loading.processingCreative')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        /* --- STANDARD QR & SHIELD TABS --- */
                        <>
                        <Box className="controls-tabs-container">
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                aria-label={t('adCreativeTools.aria.creativeToolsTabs')}
                                variant="fullWidth"
                            >
                                <Tab
                                    label={t("adCreativeTools.tabs.positionStyle")}
                                    id="ad-tools-tab-0"
                                    // disabled={isVideo}
                                    className="controls-tab"
                                />
                                <Tab
                                    id="ad-tools-tab-1"
                                    className="controls-tab"
                                    // The key change is here: the entire label is a Tooltip wrapping a span.
                                    label={
                                        <Tooltip
                                            // The tooltip is always present, but its title is conditional
                                            title={isSizingTabDisabled ? t('adCreativeTools.tooltips.sizingDisabledForVast') : ""}
                                            arrow
                                            PopperProps={{ className: "global-dark-tooltip" }}
                                        >
                                            {/* 
                                            This span is crucial. When the tab is "disabled", this span
                                            will capture the hover events that the button text inside cannot.
                                            */}
                                            <span
                                                style={{
                                                    // When disabled, make the text look faded
                                                    color: isSizingTabDisabled ? 'rgba(255, 255, 255, 0.3)' : 'inherit',
                                                    // This ensures the tooltip triggers even if the tab looks disabled
                                                    pointerEvents: 'auto',
                                                    cursor: isSizingTabDisabled ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {t("adCreativeTools.tabs.sizingSpacing")}
                                            </span>
                                        </Tooltip>
                                    }
                                    // We REMOVE the disabled prop to keep the tab element itself interactive for the tooltip
                                    disabled={isSizingTabDisabled}

                                    // We keep the onClick handler to prevent the tab from actually changing
                                    onClick={(e) => {
                                        if (isSizingTabDisabled) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        } else {
                                            // Manually trigger the tab change when it's NOT disabled
                                            handleTabChange(e, 1);
                                        }
                                    }}
                                    // This style ensures the tab fills the space correctly
                                    style={{ width: '100%', padding: 0 }}
                                />
                                <Tab
                                    label={t("adCreativeTools.tabs.label")}
                                    id="ad-tools-tab-2"
                                    disabled={config.type !== "qr"}
                                    className="controls-tab"
                                />
                            </Tabs>
                        </Box>
                        <TabPanel value={activeTab} index={0}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="position-select-label">
                                    {t("adCreativeTools.controls.positionLabel")}
                                </InputLabel>
                                <Select
                                    labelId="position-select-label"
                                    name="positionKey"
                                    value={config.positionKey}
                                    label={t("adCreativeTools.controls.positionLabel")}
                                    onChange={handleConfigChange}
                                >
                                    {POSITION_OPTIONS.map((option) => {
                                        let labelText = option.label; // Default label

                                        // Check for VIDEO scores
                                        if (isVideo && videoCornerScores && videoCornerScores[option.value] !== undefined) {
                                            labelText = `${option.label} (${Math.round(videoCornerScores[option.value])}%)`;
                                        } 
                                        // Check for IMAGE scores
                                        else if (!isVideo && liveCornerScores && liveCornerScores[option.value] !== undefined) {
                                            // liveCornerScores is already an integer from 0-100, no rounding needed.
                                            labelText = `${option.label} (${liveCornerScores[option.value]}%)`;
                                        }

                                        return (
                                            <MenuItem key={option.value} value={option.value}>
                                                {labelText}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            {colorPalette.length > 0 &&
                                config.type === "shield" && (
                                    <Box sx={{ mt: 2, mb: 1 }}>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{ mb: 1 }}
                                        >
                                            {t("adCreativeTools.controls.suggestedColorsLabel")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {colorPalette.map((color) => (
                                                <Tooltip title={color} key={color}>
                                                    <Box
                                                        className={`suggested-color-swatch ${config.shieldColor === color ? 'selected' : ''}`}
                                                        style={{ backgroundColor: color }}
                                                        onClick={() => setConfig(prev => ({ ...prev, shieldColor: color }))}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                            {config.type === "qr" ? (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="style-select-label">
                                        {t("adCreativeTools.controls.qrStyleLabel")}
                                    </InputLabel>
                                    <Select
                                        name="style"
                                        value={safeStyleValue}
                                        label={t("adCreativeTools.controls.qrStyleLabel")}
                                        onChange={handleConfigChange}
                                    >
                                        <MenuItem value="qr_black">
                                            {t("adCreativeTools.styleOptions.qr_black")}
                                        </MenuItem>
                                        <MenuItem value="qr_white">
                                            {t("adCreativeTools.styleOptions.qr_white")}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            ) : (
                                <FormControl
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                >
                                    <InputLabel htmlFor="shield-color-input">
                                        {t("adCreativeTools.controls.shieldColorLabel")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="shield-color-input"
                                        label={t("adCreativeTools.controls.shieldColorLabel")}
                                        value={config.shieldColor}
                                        readOnly
                                        onClick={(e) =>
                                            handlePickerOpen(e, "shield")
                                        }
                                        sx={{ cursor: "pointer" }}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <Box
                                                    sx={{
                                                        width: "28px",
                                                        height: "28px",
                                                        backgroundColor:
                                                            config.shieldColor,
                                                        maskImage:
                                                            "url(/static/previews/badge_template.png)",
                                                        WebkitMaskImage:
                                                            "url(/static/previews/badge_template.png)",
                                                        maskSize: "contain",
                                                        WebkitMaskSize: "contain",
                                                        maskRepeat: "no-repeat",
                                                        WebkitMaskRepeat:
                                                            "no-repeat",
                                                        maskPosition: "center",
                                                        WebkitMaskPosition:
                                                            "center",
                                                    }}
                                                />
                                            </InputAdornment>
                                        }
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title={
                                                        isEyeDropperSupported
                                                            ? t("adCreativeTools.controls.eyedropperTooltip")
                                                            : t("adCreativeTools.controls.eyedropperUnsupported")
                                                    }
                                                >
                                                    <IconButton
                                                        aria-label="pick color from screen"
                                                        onClick={handleEyeDropper}
                                                        disabled={
                                                            !isEyeDropperSupported
                                                        }
                                                        edge="end"
                                                        className="eyedropper-button"
                                                    >
                                                        <ColorizeIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        }
                                    />
                                    {displayColorPicker &&
                                        activePicker === "shield" && (
                                            <div
                                                className="color-picker-popover"
                                                ref={colorPickerRef}
                                            >
                                                <ChromePicker
                                                    color={config.shieldColor}
                                                    onChange={handleColorChange}
                                                />
                                            </div>
                                        )}
                                </FormControl>
                            )}
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <FormControl fullWidth margin="normal">
                                <Typography gutterBottom>
                                    {sizeLabelText}
                                </Typography>

                                {/* WRAP SLIDER IN TOOLTIP */}
                                <Tooltip
                                    title={sliderTooltip}
                                    arrow
                                    placement="bottom"
                                    disableHoverListener={!sliderTooltip} // Enable if tooltip text exists
                                >
                                    <span>
                                        <Slider
                                            value={config.size}
                                            onChange={(e, val) =>
                                                handleSliderChange("size", val)
                                            }
                                            min={sliderMin}
                                            max={sliderMax}
                                            step={1}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => `${value}%`}
                                        />
                                    </span>
                                </Tooltip>
                            </FormControl>
                            {config.type === "qr" && (
                                <FormControl fullWidth margin="normal">
                                    <Typography gutterBottom>
                                        {t("adCreativeTools.controls.qrPaddingLabel", { padding: config.qrPadding })}
                                    </Typography>
                                    <Slider
                                        value={config.qrPadding}
                                        onChange={(e, val) =>
                                            handleSliderChange("qrPadding", val)
                                        }
                                        min={0}
                                        max={25}
                                        step={1}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => `${value}%`}
                                    />
                                </FormControl>
                            )}
                            <FormControl fullWidth margin="normal">
                                <Typography gutterBottom>
                                    {t("adCreativeTools.controls.horizontalMarginLabel", { margin: config.paddingX })}
                                </Typography>
                                <Slider
                                    value={config.paddingX}
                                    onChange={(e, val) =>
                                        handleSliderChange("paddingX", val)
                                    }
                                    min={0}
                                    max={50}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${value}%`}
                                />
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <Typography gutterBottom>
                                    {t("adCreativeTools.controls.verticalMarginLabel", { margin: config.paddingY })}
                                </Typography>
                                <Slider
                                    value={config.paddingY}
                                    onChange={(e, val) =>
                                        handleSliderChange("paddingY", val)
                                    }
                                    min={0}
                                    max={50}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${value}%`}
                                />
                            </FormControl>
                            <div className="explanation-control-container">
                                <Switch
                                    className="custom-explanation-switch"
                                    checked={config.useRelativeMargins}
                                    onChange={handleRelativeMarginsChange}
                                    name="relativeMarginsSwitch"
                                    id="relative-margins-switch"
                                />
                                <div className="explanation-label">
                                    <Typography
                                        component="span"
                                        className="explanation-main-text"
                                    >
                                        {t("adCreativeTools.controls.useRelativeMarginsLabel")}
                                    </Typography>
                                </div>
                                <Tooltip
                                    arrow
                                    placement="right"
                                    PopperProps={{ className: "global-dark-tooltip" }}
                                    title={
                                        <React.Fragment>
                                            <Typography color="inherit" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {t("adCreativeTools.controls.marginTooltipTitle")}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                <Trans i18nKey="adCreativeTools.controls.marginTooltipOn">
                                                    <b>ON:</b> Margins are relative to the image's full width and height. Useful for responsive ads that stretch.
                                                </Trans>
                                            </Typography>
                                            <Typography variant="body2">
                                                <Trans i18nKey="adCreativeTools.controls.marginTooltipOff">
                                                    <b>OFF (Default):</b> Margins are relative to the image's shorter side. This provides uniform, predictable spacing.
                                                </Trans>
                                            </Typography>
                                        </React.Fragment>
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        className="info-icon-button"
                                    >
                                        <InfoIcon
                                            fontSize="small"
                                            className="custom-info-icon"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Typography
                                variant="caption"
                                display="block"
                                gutterBottom
                            >
                                {t("adCreativeTools.controls.labelText")}
                            </Typography>
                            <TextField
                                fullWidth
                                margin="normal"
                                label={t("adCreativeTools.controls.framePhraseLabel")}
                                name="framePhrase"
                                value={config.framePhrase}
                                onChange={handleConfigChange}
                                variant="outlined"
                                inputProps={{ maxLength: 15 }}
                                error={config.framePhrase.length >= 15}
                                helperText={
                                    config.framePhrase.length >= 15
                                        ? t("adCreativeTools.controls.framePhraseHelper", { maxLength: 15 })
                                        : `${config.framePhrase.length}/${15}`
                                }
                            />
                            <FormControl fullWidth margin="normal">
                                <InputLabel>{t("adCreativeTools.controls.labelPositionLabel")}</InputLabel>
                                <Select
                                    name="labelPosition"
                                    value={config.labelPosition}
                                    label={t("adCreativeTools.controls.labelPositionLabel")}
                                    onChange={handleConfigChange}
                                >
                                    <MenuItem value="auto">
                                        {t("adCreativeTools.controls.labelPositionAuto")}
                                    </MenuItem>
                                    <MenuItem value="top">{t("adCreativeTools.controls.labelPositionTop")}</MenuItem>
                                    <MenuItem value="bottom">{t("adCreativeTools.controls.labelPositionBottom")}</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>{t("adCreativeTools.controls.fontLabel")}</InputLabel>
                                <Select
                                    name="frameFont"
                                    value={config.frameFont}
                                    label={t("adCreativeTools.controls.fontLabel")}
                                    onChange={handleConfigChange}
                                >
                                    <MenuItem value="Roboto">Roboto</MenuItem>
                                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                                    <MenuItem value="Merriweather">Merriweather</MenuItem>
                                    <MenuItem value="Noto Serif">Noto Serif</MenuItem>
                                </Select>
                            </FormControl>
                        </TabPanel>
                        </>
                    )}

                    {/* --- ALWAYS VISIBLE GENERATE BUTTON & URL BLOCK --- */}
                    <Box sx={{ mt: 2, p: "0 8px" }}>
                        {config.type === "shield" && (
                            <>
                                <Typography
                                    variant="overline"
                                    display="block"
                                    gutterBottom
                                >
                                    {t("adCreativeTools.controls.snippetConfigTitle")}
                                </Typography>
                                <TextField
                                    fullWidth
                                    label={t("adCreativeTools.controls.urlLabel", { contentType: translatedContentType })}
                                    placeholder={t("adCreativeTools.controls.imageUrlPlaceholder")}
                                    value={adImageUrl}
                                    onChange={handleAdImageUrlChange} // <-- Use the new debounced handler
                                    variant="outlined"
                                    helperText={urlVerificationError || t("adCreativeTools.controls.urlHelper", { contentType: translatedContentType })}
                                    error={urlVerificationStatus === 'failed'} // <-- Tie error state to verification
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {urlVerificationStatus === 'verifying' && <CircularProgress size={20} />}
                                                {urlVerificationStatus === 'verified' && <CheckCircleOutlineIcon color="success" />}
                                                {urlVerificationStatus === 'failed' && <HighlightOffIcon color="error" />}
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </>
                        )}
                        {config.type !== "invisible" && (
                            <Tooltip
                                title={generateButtonTooltipText}
                                arrow
                                placement="top"
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        className="generate-button"
                                        // onClick={() => handleGenerateAssets(config)}
                                        onClick={() => handleGenerateClick(config)} 
                                        disabled={isGenerateButtonDisabled}
                                        startIcon={
                                            isLoading ? (
                                                (isVideo && (config.type === "qr" || config.type === "invisible")) ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                    <DownloadProgressDonut 
                                                        progress={downloadProgress} 
                                                        size={24} 
                                                        thickness={4.5} 
                                                        fontSize="0.5rem"
                                                        color="inherit" 
                                                    />
                                                )
                                            ) : (
                                                isCheckingLicense ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    <FileDownloadIcon />
                                                )
                                            )
                                        }
                                    >
                                        {isCheckingLicense ? t('adCreativeTools.generateButton.checkingLicense') :
                                        (isLoading
                                            ? t("adCreativeTools.generateButton.generating")
                                            : config.type === "shield"
                                                // If type is shield, check if it's a video for VAST, otherwise snippet
                                                ? isVideo
                                                    ? t("adCreativeTools.generateButton.generateVast")
                                                    : t("adCreativeTools.generateButton.generateSnippet")
                                                : config.type === "invisible"
                                                    ? t("adCreativeTools.generateButton.generateInvisible", "Secure & Download")
                                                // If type is QR, check if it's a video or image
                                                : isVideo
                                                    ? t("adCreativeTools.generateButton.generateVideo")
                                                    : t("adCreativeTools.generateButton.generateImage")
                                        )}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {isLoading && config.type !== "invisible" && (
                            <Typography
                                variant="caption"
                                display="block"
                                sx={{
                                    mt: 1.5,
                                    textAlign: "center",
                                    color: "rgba(255, 255, 255, 0.6)",
                                    fontStyle: "italic",
                                    animation: "pulse 2s infinite" // Optional: makes it breathe slightly
                                }}
                            >
                                {isVideo 
                                    ? t('adCreativeTools.loading.processingVideo')
                                    : t('adCreativeTools.loading.processingCreative')
                                }
                            </Typography>
                        )}
                    </Box>
                </Box>
            </div>

            {generationError && (
                <Typography color="error" className="generation-error">
                    {t('adCreativeTools.labels.generationErrorPrefix')}{generationError}
                </Typography>
            )}

            {generationSuccessMsg && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mt: 2, 
                        textAlign: 'left', 
                        alignItems: 'center' /* Centers the checkmark with the text */
                    }}
                >
                    {generationSuccessMsg}
                </Alert>
            )}

            {generatedSnippet && (
                <div className="snippet-area">
                    {isSocialSource && isVideo && config.type === 'shield' && (
                        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left', marginBottom: '1rem', alignItems: 'center' }} className="vast-warning-alert">
                            <p>
                                <Trans i18nKey="adCreativeTools.errors.vastWarningIntro">
                                    <strong />
                                    <strong />
                                </Trans>
                            </p>
                            <p>{t('adCreativeTools.errors.vastWarningMethodsHeader')}</p>
                            <ul>
                                <li>{t('adCreativeTools.errors.vastWarningMethod1')}</li>
                                <li>{t('adCreativeTools.errors.vastWarningMethod2')}</li>
                            </ul>
                        </Alert>
                    )}
                    <Typography variant="overline">
                        {isVideo && config.type === 'shield'
                            ? t("adCreativeTools.snippetArea.vastSnippetTitle")
                            : t("adCreativeTools.snippetArea.title")
                        }
                    </Typography>
                    <div className="snippet-wrapper">
                        <Tooltip
                            title={
                                copySuccess ? t("adCreativeTools.snippetArea.copySuccess") : t("adCreativeTools.snippetArea.copy")
                            }
                            placement="top"
                        >
                            <IconButton
                                onClick={handleCopySnippet}
                                className="copy-button"
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                        <SyntaxHighlighter
                            language={isVideo && config.type === 'shield' ? 'xml' : 'html'}
                            style={vscDarkPlus}
                            customStyle={{
                                margin: 0,
                                padding: "1rem",
                                maxHeight: "250px", // Apply height limit here
                                overflow: "auto",   // Apply scrolling here
                                borderRadius: "8px", // Ensure background respects the parent's rounding
                            }}
                        >
                            {generatedSnippet}
                        </SyntaxHighlighter>
                    </div>
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleTestSnippet}
                            className="outlined-action-button"
                        >
                            {t("adCreativeTools.snippetArea.testButton")}
                        </Button>
                    </Box>
                </div>
            )}

            <LicenseModal 
                isOpen={isLicenseModalOpen}
                onClose={() => !isPaying && setIsLicenseModalOpen(false)}
                onConfirm={handleLicensePayment}
                onDownload={handleModalDownload}
                isPaying={isPaying}
                isLoading={isCheckingLicense}
                currentLicense={licenseStatus}
                contentType={contentType}
                pricingConfig={pricingConfig}
                currencyRates={currencyRates}
                selectedCurrency={user?.preferred_currency || 'USD'}
            />

        </div>
    );
};

export default AdCreativeTools;